import React from "react";
import { Link } from "react-router-dom";

import type {
  DashboardFamily,
  EquityDashboardRow,
  HoldingState,
  Portfolio,
  PortfolioPositionDetailUpsertRequest,
  PortfolioPositionStage,
  PortfolioReviewDecision,
  PortfolioUpsertRequest,
} from "@features/investments/api";
import { GlossaryTerm } from "@features/investments/glossary/term";
import { InvestmentsWorkspaceShell } from "@features/investments/components/workspace-shell";
import { InvestmentsLoadingScreen, InvestmentsPageState } from "@features/investments/components/page-state";
import {
  useCreatePortfolioMutation,
  useCreatePortfolioPositionReviewMutation,
  useDeletePortfolioMutation,
  useInvestmentsAccessQuery,
  useInvestmentsDashboardQuery,
  useInvestmentsPortfolioQuery,
  usePortfolioPositionDetailQuery,
  usePortfolioPositionReviewsQuery,
  useUpdatePortfolioMutation,
  useUpdatePortfolioPositionDetailMutation,
  useWatchlistLiveQuotesQuery,
} from "@features/investments/queries";
import { useActivePortfolioSelection } from "@features/investments/state";
import { INVESTMENTS_PORTFOLIO_POSITION_LIMIT } from "@features/investments/state/constants";
import "@views/investments/index.scss";
import { equityRoute } from "@features/investments/routes";

type EditorMode = "create" | "edit";
type EditorPosition = {
  ticker: string;
  stage: PortfolioPositionStage;
};
type EditorState = {
  mode: EditorMode;
  portfolioID: number | null;
  name: string;
  notes: string;
  positions: EditorPosition[];
};
type WorkflowDraft = {
  stage: PortfolioPositionStage;
  thesisSummary: string;
  whyNow: string;
  keyRisk: string;
  catalysts: string;
  disconfirmingEvidence: string;
  nextReviewAt: string;
};
type ReviewDraft = {
  decision: PortfolioReviewDecision;
  summaryNote: string;
  nextReviewAt: string;
};
type TickerOption = {
  ticker: string;
  companyName: string;
};

const WATCHLIST_MODAL_ANIMATION_MS = 180;
const EMPTY_EDITOR_STATE: EditorState = {
  mode: "create",
  portfolioID: null,
  name: "",
  notes: "",
  positions: [],
};
const EMPTY_WORKFLOW_DRAFT: WorkflowDraft = {
  stage: "CANDIDATE",
  thesisSummary: "",
  whyNow: "",
  keyRisk: "",
  catalysts: "",
  disconfirmingEvidence: "",
  nextReviewAt: "",
};
const EMPTY_REVIEW_DRAFT: ReviewDraft = {
  decision: "REVIEWED",
  summaryNote: "",
  nextReviewAt: "",
};

const PortfolioScreen = (): JSX.Element => {
  const accessQuery = useInvestmentsAccessQuery();
  const dashboardQuery = useInvestmentsDashboardQuery();
  const portfolioQuery = useInvestmentsPortfolioQuery(true);
  const createMutation = useCreatePortfolioMutation();
  const updateMutation = useUpdatePortfolioMutation();
  const deleteMutation = useDeletePortfolioMutation();
  const updateDetailMutation = useUpdatePortfolioPositionDetailMutation();
  const createReviewMutation = useCreatePortfolioPositionReviewMutation();
  const portfolios = portfolioQuery.data?.portfolios ?? [];
  const equityFamilies = dashboardQuery.data?.equityFamilies ?? [];
  const equityRows = dashboardQuery.data?.equityRows ?? [];
  const [selectedFamilyKey, setSelectedFamilyKey] = React.useState("");
  const [tickerSearch, setTickerSearch] = React.useState("");
  const [editor, setEditor] = React.useState<EditorState | null>(null);
  const [editorClosing, setEditorClosing] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");
  const [deleteError, setDeleteError] = React.useState("");
  const [selectionNotice, setSelectionNotice] = React.useState("");
  const [workflowDraft, setWorkflowDraft] = React.useState<WorkflowDraft>(EMPTY_WORKFLOW_DRAFT);
  const [reviewDraft, setReviewDraft] = React.useState<ReviewDraft>(EMPTY_REVIEW_DRAFT);
  const [workflowError, setWorkflowError] = React.useState("");
  const [reviewError, setReviewError] = React.useState("");
  const [selectedPositionTicker, setSelectedPositionTicker] = React.useState("");
  const busy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const closeTimeoutRef = React.useRef<number | null>(null);
  const [selectedPortfolioID, setSelectedPortfolioID] = useActivePortfolioSelection(portfolios.map((portfolio) => portfolio.id));
  const selectedPortfolio = React.useMemo(
    () => portfolios.find((portfolio) => portfolio.id === selectedPortfolioID) ?? null,
    [portfolios, selectedPortfolioID],
  );
  const canUseFmp = accessQuery.data?.canUseFmp === true;
  const requestedLiveQuoteTickers = React.useMemo(
    () => normalizeTickers(selectedPortfolio?.tickers ?? []),
    [selectedPortfolio?.tickers],
  );
  const liveQuotesQuery = useWatchlistLiveQuotesQuery(requestedLiveQuoteTickers, canUseFmp && requestedLiveQuoteTickers.length > 0);
  const liveQuoteMap = React.useMemo(() => {
    const entries = (liveQuotesQuery.data?.quotes ?? []).map((quote) => [quote.ticker.toUpperCase(), quote.price] as const);
    return new Map(entries);
  }, [liveQuotesQuery.data]);
  const liveQuoteCount = React.useMemo(
    () => new Set((liveQuotesQuery.data?.quotes ?? []).map((quote) => quote.ticker.toUpperCase())).size,
    [liveQuotesQuery.data],
  );
  const selectedRows = React.useMemo(
    () => (selectedPortfolio?.rows ?? []).map((row) => ({
      ...row,
      currentPrice: liveQuoteMap.get(row.ticker.toUpperCase()) ?? row.currentPrice,
    })),
    [liveQuoteMap, selectedPortfolio],
  );
  const selectedPositionRow = React.useMemo(
    () => selectedRows.find((row) => row.ticker === selectedPositionTicker) ?? null,
    [selectedPositionTicker, selectedRows],
  );
  const detailQuery = usePortfolioPositionDetailQuery(selectedPortfolio?.id ?? null, selectedPositionTicker || null, selectedPortfolio != null && selectedPositionTicker !== "");
  const reviewsQuery = usePortfolioPositionReviewsQuery(selectedPortfolio?.id ?? null, selectedPositionTicker || null, selectedPortfolio != null && selectedPositionTicker !== "");
  const selectedFamily = React.useMemo(
    () => equityFamilies.find((family) => family.key === selectedFamilyKey) ?? null,
    [equityFamilies, selectedFamilyKey],
  );
  const tickerOptions = React.useMemo(() => buildTickerOptions(equityRows), [equityRows]);
  const selectedTickers = React.useMemo(
    () => new Set((editor?.positions ?? []).map((position) => position.ticker)),
    [editor?.positions],
  );
  const filteredTickerOptions = React.useMemo(
    () => filterTickerOptions(tickerOptions, tickerSearch, selectedTickers),
    [tickerOptions, tickerSearch, selectedTickers],
  );
  const selectedPositionCount = editor?.positions.length ?? 0;
  const activeResearchRows = React.useMemo(() => selectedRows.filter((row) => row.stage === "ACTIVE_RESEARCH"), [selectedRows]);
  const candidateRows = React.useMemo(() => selectedRows.filter((row) => row.stage === "CANDIDATE"), [selectedRows]);
  const ownedRows = React.useMemo(() => selectedRows.filter((row) => row.stage === "OWNED"), [selectedRows]);

  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current != null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (editor == null) {
      return undefined;
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape" && !busy) {
        closeEditor();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [busy, editor]);

  React.useEffect(() => {
    if (selectedRows.length === 0) {
      setSelectedPositionTicker("");
      return;
    }
    if (selectedRows.some((row) => row.ticker === selectedPositionTicker)) {
      return;
    }
    setSelectedPositionTicker(selectedRows[0]?.ticker ?? "");
  }, [selectedPositionTicker, selectedRows]);

  React.useEffect(() => {
    if (!detailQuery.data) {
      setWorkflowDraft(EMPTY_WORKFLOW_DRAFT);
      return;
    }
    setWorkflowDraft({
      stage: detailQuery.data.stage,
      thesisSummary: detailQuery.data.thesisSummary || "",
      whyNow: detailQuery.data.whyNow || "",
      keyRisk: detailQuery.data.keyRisk || "",
      catalysts: detailQuery.data.catalysts || "",
      disconfirmingEvidence: detailQuery.data.disconfirmingEvidence || "",
      nextReviewAt: toDateInputValue(detailQuery.data.nextReviewAt ?? null),
    });
  }, [detailQuery.data]);

  function openCreateEditor(): void {
    setEditor({ ...EMPTY_EDITOR_STATE });
    setEditorClosing(false);
    setSubmitError("");
    setSelectionNotice("");
    setTickerSearch("");
    setSelectedFamilyKey("");
  }

  function openEditEditor(portfolio: Portfolio): void {
    setEditor({
      mode: "edit",
      portfolioID: portfolio.id,
      name: portfolio.name,
      notes: portfolio.notes,
      positions: portfolio.positions.map((position) => ({
        ticker: position.ticker,
        stage: position.stage,
      })),
    });
    setEditorClosing(false);
    setSubmitError("");
    setSelectionNotice("");
    setTickerSearch("");
    setSelectedFamilyKey("");
  }

  function closeEditor(): void {
    if (editor == null || editorClosing) {
      return;
    }
    setEditorClosing(true);
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = window.setTimeout(() => {
      closeEditorImmediately();
    }, WATCHLIST_MODAL_ANIMATION_MS);
  }

  function closeEditorImmediately(): void {
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setEditor(null);
    setEditorClosing(false);
    setSubmitError("");
    setSelectionNotice("");
    setTickerSearch("");
    setSelectedFamilyKey("");
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (editor == null || busy) {
      return;
    }
    setSubmitError("");
    const payload: PortfolioUpsertRequest = {
      name: editor.name,
      notes: editor.notes,
      positions: editor.positions.map((position) => ({
        ticker: position.ticker,
        stage: position.stage,
        holdingState: holdingStateFromStage(position.stage),
      })),
    };
    try {
      const portfolio = editor.mode === "create"
        ? await createMutation.mutateAsync(payload)
        : await updateMutation.mutateAsync({
          portfolioID: editor.portfolioID as number,
          payload,
        });
      setSelectedPortfolioID(portfolio.id);
      closeEditor();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to save portfolio.");
    }
  }

  async function handleDelete(portfolio: Portfolio): Promise<void> {
    if (busy || !window.confirm(`Delete portfolio "${portfolio.name}"?`)) {
      return;
    }
    setDeleteError("");
    try {
      await deleteMutation.mutateAsync(portfolio.id);
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : "Failed to delete portfolio.");
    }
  }

  async function handleSaveWorkflow(): Promise<void> {
    if (selectedPortfolio == null || selectedPositionRow == null) {
      return;
    }
    setWorkflowError("");
    const payload: PortfolioPositionDetailUpsertRequest = {
      stage: workflowDraft.stage,
      thesisSummary: workflowDraft.thesisSummary,
      whyNow: workflowDraft.whyNow,
      keyRisk: workflowDraft.keyRisk,
      catalysts: workflowDraft.catalysts,
      disconfirmingEvidence: workflowDraft.disconfirmingEvidence,
      nextReviewAt: workflowDraft.nextReviewAt ? new Date(`${workflowDraft.nextReviewAt}T00:00:00`).toISOString() : null,
    };
    try {
      await updateDetailMutation.mutateAsync({
        portfolioID: selectedPortfolio.id,
        ticker: selectedPositionRow.ticker,
        payload,
      });
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : "Failed to update workflow detail.");
    }
  }

  async function handleCreateReview(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (selectedPortfolio == null || selectedPositionRow == null) {
      return;
    }
    setReviewError("");
    try {
      await createReviewMutation.mutateAsync({
        portfolioID: selectedPortfolio.id,
        ticker: selectedPositionRow.ticker,
        payload: {
          decision: reviewDraft.decision,
          summaryNote: reviewDraft.summaryNote,
          nextReviewAt: reviewDraft.nextReviewAt ? new Date(`${reviewDraft.nextReviewAt}T00:00:00`).toISOString() : null,
        },
      });
      setReviewDraft(EMPTY_REVIEW_DRAFT);
    } catch (error) {
      setReviewError(error instanceof Error ? error.message : "Failed to add review entry.");
    }
  }

  function addTicker(ticker: string): void {
    const current = editor ?? { ...EMPTY_EDITOR_STATE };
    if (current.positions.some((position) => position.ticker === ticker)) {
      setTickerSearch("");
      return;
    }
    if (current.positions.length >= INVESTMENTS_PORTFOLIO_POSITION_LIMIT) {
      setSelectionNotice(buildTickerLimitNotice());
      setTickerSearch("");
      return;
    }
    setEditor({
      ...current,
      positions: [...current.positions, { ticker, stage: "ACTIVE_RESEARCH" }],
    });
    setSelectionNotice("");
    setTickerSearch("");
  }

  function addFamilyTickers(): void {
    if (selectedFamily == null) {
      return;
    }
    const current = editor ?? { ...EMPTY_EDITOR_STATE };
    const next = [...current.positions];
    let limitReached = false;
    for (const ticker of selectedFamily.tickers ?? []) {
      if (next.some((position) => position.ticker === ticker)) {
        continue;
      }
      if (next.length >= INVESTMENTS_PORTFOLIO_POSITION_LIMIT) {
        limitReached = true;
        break;
      }
      next.push({ ticker, stage: "ACTIVE_RESEARCH" });
    }
    setEditor({ ...current, positions: next });
    setSelectionNotice(limitReached ? buildTickerLimitNotice() : "");
  }

  function removeTicker(ticker: string): void {
    if (editor == null) {
      return;
    }
    setEditor({
      ...editor,
      positions: editor.positions.filter((position) => position.ticker !== ticker),
    });
    setSelectionNotice("");
  }

  function setPositionStage(ticker: string, stage: PortfolioPositionStage): void {
    setEditor((prev) => {
      if (prev == null) {
        return prev;
      }
      return {
        ...prev,
        positions: prev.positions.map((position) => position.ticker === ticker ? { ...position, stage } : position),
      };
    });
  }

  function movePosition(ticker: string, direction: -1 | 1): void {
    setEditor((prev) => {
      if (prev == null) {
        return prev;
      }
      const currentIndex = prev.positions.findIndex((position) => position.ticker === ticker);
      const nextIndex = currentIndex + direction;
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= prev.positions.length) {
        return prev;
      }
      const positions = [...prev.positions];
      const [moved] = positions.splice(currentIndex, 1);
      positions.splice(nextIndex, 0, moved);
      return {
        ...prev,
        positions,
      };
    });
  }

  const editorTickerCards = React.useMemo(
    () => (editor?.positions ?? []).map((position) => ({
      ...position,
      companyName: tickerOptions.find((option) => option.ticker === position.ticker)?.companyName ?? position.ticker,
    })),
    [editor?.positions, tickerOptions],
  );
  const liveQuoteNote = React.useMemo(() => {
    if (selectedPortfolio == null || requestedLiveQuoteTickers.length === 0) {
      return "Portfolio prices use the latest cached snapshot until a portfolio is selected.";
    }
    if (!canUseFmp) {
      return "Operator workflow is available, but current prices are using cached snapshot data because live quote access is unavailable in this account.";
    }
    if (liveQuotesQuery.error) {
      return "Live prices unavailable right now; showing cached current prices where available.";
    }
    if (liveQuotesQuery.data != null && liveQuoteCount < requestedLiveQuoteTickers.length) {
      return "Some live prices unavailable right now; showing cached current prices where needed.";
    }
    return "Live prices update every 30s for the selected portfolio. Cached prices remain the fallback.";
  }, [canUseFmp, liveQuoteCount, liveQuotesQuery.data, liveQuotesQuery.error, requestedLiveQuoteTickers.length, selectedPortfolio]);

  if (dashboardQuery.isLoading || portfolioQuery.isLoading || accessQuery.isLoading) {
    return <InvestmentsLoadingScreen message="Loading portfolio workspace..." />;
  }

  if (dashboardQuery.error) {
    return <InvestmentsPageState message={`Failed to load equity universe: ${dashboardQuery.error.message}`} />;
  }

  if (portfolioQuery.error) {
    return <InvestmentsPageState message={`Failed to load portfolio workspace: ${portfolioQuery.error.message}`} />;
  }

  return (
    <InvestmentsWorkspaceShell
      title="Portfolio"
      description="Operator Mode tracks active research, candidates, owned names, thesis notes, and review history in one workflow."
      generatedAt={dashboardQuery.data?.generatedAt}
      modeLabel="Operator Mode"
    >
      {deleteError && <div className="ticker-refresh-status error">{deleteError}</div>}

      <div className="watchlist-layout">
        <section className="investments-card watchlist-portfolio-panel">
          <div className="watchlist-panel-header">
            <div>
              <h2>Saved Portfolios</h2>
              <p>Portfolios define the active operator workflow. Use them to separate active research, candidate buys, and owned positions.</p>
            </div>
            <button type="button" className="watchlist-family-add-btn" onClick={openCreateEditor}>
              New Portfolio
            </button>
          </div>

          {portfolios.length === 0 ? (
            <div className="watchlist-empty-state">
              <strong>No saved portfolios yet.</strong>
              <p>Create your first portfolio to organize active research, candidates, and owned names.</p>
            </div>
          ) : (
            <div className="watchlist-portfolio-grid">
              {portfolios.map((portfolio) => {
                const counts = summarizePortfolio(portfolio);
                return (
                  <article key={portfolio.id} className={`watchlist-portfolio-card ${selectedPortfolioID === portfolio.id ? "selected" : ""}`}>
                    <button
                      type="button"
                      className="watchlist-portfolio-card-select"
                      aria-pressed={selectedPortfolioID === portfolio.id}
                      onClick={() => setSelectedPortfolioID(portfolio.id)}
                    >
                      <div className="watchlist-portfolio-card-header">
                        <div>
                          <h3>{portfolio.name}</h3>
                          <div className="watchlist-portfolio-card-meta">
                            <span>{counts.activeResearch} active research</span>
                            <span>{counts.candidates} candidate</span>
                            <span>{counts.owned} owned</span>
                          </div>
                        </div>
                      </div>
                      {portfolio.notes && <p className="watchlist-portfolio-card-notes">{portfolio.notes}</p>}
                      <div className="watchlist-portfolio-card-tickers">{portfolio.tickers.join(", ")}</div>
                    </button>
                    <div className="watchlist-portfolio-card-actions">
                      <button type="button" className="watchlist-remove-btn" onClick={() => openEditEditor(portfolio)}>
                        Edit
                      </button>
                      <button type="button" className="watchlist-remove-btn" onClick={() => void handleDelete(portfolio)}>
                        Delete
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="investments-card investments-family-section watchlist-detail-section">
          <div className="watchlist-panel-header">
            <div>
              <h2>Portfolio Workflow</h2>
              <p>
                {selectedPortfolio
                  ? `${selectedPortfolio.name} is the current operator portfolio.`
                  : "Select or create a portfolio to organize active research, candidates, and owned names."}
              </p>
            </div>
            {selectedPortfolio && <div className="workflow-section-meta">Active</div>}
          </div>

          {selectedPortfolio == null ? (
            <div className="watchlist-detail-empty">Select a portfolio to review workflow stages and thesis details.</div>
          ) : (
            <>
              <p className="buy-levels-note">{liveQuoteNote}</p>
              <div className="portfolio-stage-grid">
                <StageColumn title="Active Research" rows={activeResearchRows} selectedTicker={selectedPositionTicker} onSelect={setSelectedPositionTicker} emptyMessage="No active research names yet." />
                <StageColumn title="Candidate" rows={candidateRows} selectedTicker={selectedPositionTicker} onSelect={setSelectedPositionTicker} emptyMessage="No candidate names yet." />
                <StageColumn title="Owned" rows={ownedRows} selectedTicker={selectedPositionTicker} onSelect={setSelectedPositionTicker} emptyMessage="No owned positions yet." />
              </div>
            </>
          )}
        </section>
      </div>

      <section className="workflow-section">
        <div className="workflow-section-header">
          <div>
            <h2>Workflow Detail</h2>
            <p>Capture the current thesis, key risk, catalysts, disconfirming evidence, and review history for the selected position.</p>
          </div>
          {selectedPositionRow && (
            <div className="workflow-section-meta">
              <Link to={equityRoute(selectedPositionRow.ticker)}>{selectedPositionRow.ticker}</Link>
            </div>
          )}
        </div>

        {selectedPortfolio == null || selectedPositionRow == null ? (
          <div className="investments-card workflow-card workflow-empty">Select a portfolio position to edit its workflow detail.</div>
        ) : (
          <div className="portfolio-workflow-grid">
            <section className="investments-card workflow-card portfolio-workflow-card">
              <div className="watchlist-panel-header">
                <div>
                  <h3>{selectedPositionRow.ticker}</h3>
                  <p>{selectedPositionRow.companyName}</p>
                </div>
                <div>{portfolioActionPill(selectedPositionRow.recommendedAction)}</div>
              </div>
              <div className="portfolio-workflow-metrics">
                <div className="portfolio-workflow-metric">
                  <span><GlossaryTerm label="Current Price" variant="metric" /></span>
                  <strong>{formatCurrency(selectedPositionRow.currentPrice)}</strong>
                </div>
                <div className="portfolio-workflow-metric">
                  <span><GlossaryTerm label="Target Buy" variant="metric" /></span>
                  <strong>{formatCurrency(selectedPositionRow.targetBuyPrice)}</strong>
                </div>
                <div className="portfolio-workflow-metric">
                  <span><GlossaryTerm label="Recommended Action" variant="metric" /></span>
                  <strong>{selectedPositionRow.recommendedAction.replace(/_/g, " ")}</strong>
                </div>
                <div className="portfolio-workflow-metric">
                  <span><GlossaryTerm label="Next Review" variant="metric" /></span>
                  <strong>{formatDateLabel(detailQuery.data?.nextReviewAt ?? null)}</strong>
                </div>
              </div>
              {detailQuery.isLoading ? (
                <div className="workflow-empty">Loading workflow detail...</div>
              ) : detailQuery.error ? (
                <div className="ticker-refresh-status error">{detailQuery.error.message}</div>
              ) : (
                <>
                  <div className="portfolio-workflow-form">
                    <label className="watchlist-field">
                      <span>Stage</span>
                      <select
                        className="watchlist-select"
                        value={workflowDraft.stage}
                        onChange={(event) => setWorkflowDraft((current) => ({ ...current, stage: event.target.value as PortfolioPositionStage }))}
                      >
                        <option value="ACTIVE_RESEARCH">Active Research</option>
                        <option value="CANDIDATE">Candidate</option>
                        <option value="OWNED">Owned</option>
                      </select>
                    </label>
                    <label className="watchlist-field">
                      <span>Thesis Summary</span>
                      <textarea className="watchlist-textarea" value={workflowDraft.thesisSummary} onChange={(event) => setWorkflowDraft((current) => ({ ...current, thesisSummary: event.target.value }))} />
                    </label>
                    <label className="watchlist-field">
                      <span>Why Now</span>
                      <textarea className="watchlist-textarea" value={workflowDraft.whyNow} onChange={(event) => setWorkflowDraft((current) => ({ ...current, whyNow: event.target.value }))} />
                    </label>
                    <label className="watchlist-field">
                      <span>Key Risk</span>
                      <textarea className="watchlist-textarea" value={workflowDraft.keyRisk} onChange={(event) => setWorkflowDraft((current) => ({ ...current, keyRisk: event.target.value }))} />
                    </label>
                    <label className="watchlist-field">
                      <span>Catalysts</span>
                      <textarea className="watchlist-textarea" value={workflowDraft.catalysts} onChange={(event) => setWorkflowDraft((current) => ({ ...current, catalysts: event.target.value }))} />
                    </label>
                    <label className="watchlist-field">
                      <span>Disconfirming Evidence</span>
                      <textarea className="watchlist-textarea" value={workflowDraft.disconfirmingEvidence} onChange={(event) => setWorkflowDraft((current) => ({ ...current, disconfirmingEvidence: event.target.value }))} />
                    </label>
                    <label className="watchlist-field">
                      <span>Next Review Date</span>
                      <input className="watchlist-input" type="date" value={workflowDraft.nextReviewAt} onChange={(event) => setWorkflowDraft((current) => ({ ...current, nextReviewAt: event.target.value }))} />
                    </label>
                  </div>
                  {workflowError && <div className="ticker-refresh-status error">{workflowError}</div>}
                  <div className="watchlist-editor-actions">
                    <button type="button" className="watchlist-editor-submit-btn" onClick={() => void handleSaveWorkflow()} disabled={updateDetailMutation.isPending}>
                      {updateDetailMutation.isPending ? "Saving..." : "Save Workflow Detail"}
                    </button>
                  </div>
                </>
              )}
            </section>

            <section className="investments-card workflow-card portfolio-review-card">
              <div className="watchlist-panel-header">
                <div>
                  <h3>Review History</h3>
                  <p>Capture the decision made at each review and set the next date if needed.</p>
                </div>
              </div>
              <form className="portfolio-review-form" onSubmit={(event) => void handleCreateReview(event)}>
                <label className="watchlist-field">
                  <span>Decision</span>
                  <select
                    className="watchlist-select"
                    value={reviewDraft.decision}
                    onChange={(event) => setReviewDraft((current) => ({ ...current, decision: event.target.value as PortfolioReviewDecision }))}
                  >
                    <option value="REVIEWED">Reviewed</option>
                    <option value="PROMOTE">Promote</option>
                    <option value="BUY">Buy</option>
                    <option value="HOLD">Hold</option>
                    <option value="TRIM">Trim</option>
                    <option value="EXIT">Exit</option>
                    <option value="PASS">Pass</option>
                  </select>
                </label>
                <label className="watchlist-field">
                  <span>Summary</span>
                  <textarea className="watchlist-textarea" value={reviewDraft.summaryNote} onChange={(event) => setReviewDraft((current) => ({ ...current, summaryNote: event.target.value }))} />
                </label>
                <label className="watchlist-field">
                  <span>Next Review Date</span>
                  <input className="watchlist-input" type="date" value={reviewDraft.nextReviewAt} onChange={(event) => setReviewDraft((current) => ({ ...current, nextReviewAt: event.target.value }))} />
                </label>
                {reviewError && <div className="ticker-refresh-status error">{reviewError}</div>}
                <div className="watchlist-editor-actions">
                  <button type="submit" className="watchlist-editor-submit-btn" disabled={createReviewMutation.isPending}>
                    {createReviewMutation.isPending ? "Adding..." : "Add Review Entry"}
                  </button>
                </div>
              </form>

              {reviewsQuery.isLoading ? (
                <div className="workflow-empty">Loading review history...</div>
              ) : reviewsQuery.error ? (
                <div className="ticker-refresh-status error">{reviewsQuery.error.message}</div>
              ) : (reviewsQuery.data?.reviews ?? []).length === 0 ? (
                <div className="workflow-empty">No review history yet.</div>
              ) : (
                <div className="portfolio-review-list">
                  {(reviewsQuery.data?.reviews ?? []).map((review) => (
                    <article key={review.id} className="portfolio-review-entry">
                      <div className="portfolio-review-entry-top">
                        <strong>{review.decision.replace(/_/g, " ")}</strong>
                        <span>{formatDateLabel(review.createdAt)}</span>
                      </div>
                      <p>{review.summaryNote}</p>
                      {review.nextReviewAt && <div className="portfolio-review-next">Next review: {formatDateLabel(review.nextReviewAt)}</div>}
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </section>

      {editor && (
        <div
          className={`watchlist-modal-backdrop${editorClosing ? " closing" : ""}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy) {
              closeEditor();
            }
          }}
        >
          <section className={`investments-card watchlist-editor-panel watchlist-modal${editorClosing ? " closing" : ""}`} role="dialog" aria-modal="true" aria-labelledby="watchlist-modal-title">
            <div className="watchlist-modal-header">
              <div className="watchlist-panel-header">
                <div>
                  <h2 id="watchlist-modal-title">{editor.mode === "edit" ? "Edit Portfolio" : "Create Portfolio"}</h2>
                  <p>Pick configured equities, then place each one into active research, candidate, or owned workflow.</p>
                </div>
                <div className="watchlist-editor-badges" aria-hidden="true">
                  <span className="watchlist-guidance-chip">Operator workflow</span>
                  <span className="watchlist-guidance-chip">Order preserved</span>
                  <span className="watchlist-guidance-chip">Stage-based</span>
                </div>
              </div>
              <button type="button" className="watchlist-modal-close" onClick={closeEditor} disabled={busy}>
                Close
              </button>
            </div>

            <form className="watchlist-editor-form" onSubmit={(event) => void handleSubmit(event)}>
              <label className="watchlist-field">
                <span>Name</span>
                <input className="watchlist-input" type="text" value={editor.name} onChange={(event) => setEditor((prev) => prev ? { ...prev, name: event.target.value } : prev)} required />
              </label>

              <label className="watchlist-field">
                <span>Notes</span>
                <textarea className="watchlist-textarea watchlist-notes-input" value={editor.notes} onChange={(event) => setEditor((prev) => prev ? { ...prev, notes: event.target.value } : prev)} placeholder="Describe the purpose of this portfolio." />
              </label>

              <div className="watchlist-field">
                <span>Universe</span>
                <div className="watchlist-selector-shell">
                  <div className="watchlist-family-toolbar">
                    <label className="watchlist-inline-field">
                      <span>Family</span>
                      <select className="watchlist-select" value={selectedFamilyKey} onChange={(event) => setSelectedFamilyKey(event.target.value)}>
                        <option value="">Choose family</option>
                        {equityFamilies.map((family) => (
                          <option key={family.key} value={family.key}>{family.name}</option>
                        ))}
                      </select>
                    </label>
                    <button type="button" className="watchlist-family-add-btn" onClick={addFamilyTickers} disabled={selectedFamily == null}>
                      Add Family
                    </button>
                  </div>

                  <label className="watchlist-inline-field watchlist-search-combobox">
                    <span>Search configured equities</span>
                    <input className="watchlist-input" type="text" value={tickerSearch} placeholder="Search by ticker or company" onChange={(event) => setTickerSearch(event.target.value)} />
                    {tickerSearch.trim() !== "" && (
                      <div className="watchlist-search-results" role="list" aria-label="Ticker search results">
                        {filteredTickerOptions.length > 0 ? (
                          filteredTickerOptions.map((option) => (
                            <button
                              key={option.ticker}
                              type="button"
                              className="watchlist-search-result"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                addTicker(option.ticker);
                              }}
                            >
                              <span className="watchlist-search-ticker">{option.ticker}</span>
                              <span className="watchlist-search-company">{option.companyName}</span>
                            </button>
                          ))
                        ) : (
                          <div className="watchlist-search-empty">No configured equities match that search.</div>
                        )}
                      </div>
                    )}
                  </label>
                </div>
                {selectionNotice && <p className="watchlist-selection-note">{selectionNotice}</p>}
              </div>

              <div className="watchlist-field">
                <div className="watchlist-field-header">
                  <span>Selected Positions</span>
                  <span className={`watchlist-selection-count${selectedPositionCount >= INVESTMENTS_PORTFOLIO_POSITION_LIMIT ? " is-full" : ""}`}>
                    {selectedPositionCount} / {INVESTMENTS_PORTFOLIO_POSITION_LIMIT} selected
                  </span>
                </div>
                {editorTickerCards.length === 0 ? (
                  <div className="watchlist-selection-empty">Add tickers from a family or search the configured equity universe.</div>
                ) : (
                  <div className="watchlist-selected-scroll">
                    <div className="watchlist-selected-grid">
                      {editorTickerCards.map((position, index) => (
                        <div key={position.ticker} className="watchlist-selected-card">
                          <div className="watchlist-selected-copy">
                            <div className="watchlist-selected-order">#{index + 1}</div>
                            <div className="watchlist-selected-ticker">{position.ticker}</div>
                            <div className="watchlist-selected-company">{position.companyName}</div>
                          </div>
                          <div className="watchlist-selected-actions">
                            <div className="watchlist-selected-order-controls">
                              <button type="button" className="watchlist-order-btn" aria-label={`Move ${position.ticker} up`} disabled={index === 0} onClick={() => movePosition(position.ticker, -1)}>
                                Up
                              </button>
                              <button type="button" className="watchlist-order-btn" aria-label={`Move ${position.ticker} down`} disabled={index === editorTickerCards.length - 1} onClick={() => movePosition(position.ticker, 1)}>
                                Down
                              </button>
                            </div>
                            <select className="watchlist-position-select" aria-label={`${position.ticker} workflow stage`} value={position.stage} onChange={(event) => setPositionStage(position.ticker, event.target.value as PortfolioPositionStage)}>
                              <option value="ACTIVE_RESEARCH">Active Research</option>
                              <option value="CANDIDATE">Candidate</option>
                              <option value="OWNED">Owned</option>
                            </select>
                            <button type="button" className="watchlist-remove-btn" onClick={() => removeTicker(position.ticker)}>
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {submitError && <div className="ticker-refresh-status error">{submitError}</div>}

              <div className="watchlist-editor-footer">
                <p className="watchlist-editor-footnote">
                  Active research names are still being underwritten. Candidate names are worth watching for entry. Owned names are active holdings that need review discipline.
                </p>
                <div className="watchlist-editor-actions">
                  <button type="submit" className="watchlist-editor-submit-btn" disabled={busy}>
                    {editor.mode === "edit" ? "Save Portfolio" : "Create Portfolio"}
                  </button>
                  <button type="button" className="watchlist-remove-btn" onClick={closeEditor} disabled={busy}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      )}
    </InvestmentsWorkspaceShell>
  );
};

function StageColumn({
  title,
  rows,
  selectedTicker,
  onSelect,
  emptyMessage,
}: {
  title: string;
  rows: Portfolio["rows"];
  selectedTicker: string;
  onSelect: (ticker: string) => void;
  emptyMessage: string;
}): JSX.Element {
  return (
    <section className="investments-card workflow-card portfolio-stage-column">
      <div className="workflow-column-header">
        <div>
          <div className="workflow-column-title">{title}</div>
          <div className="workflow-column-count">{rows.length} ticker{rows.length === 1 ? "" : "s"}</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="workflow-empty">{emptyMessage}</div>
      ) : (
        <div className="workflow-card-list">
          {rows.map((row) => (
            <button
              key={row.ticker}
              type="button"
              className={`portfolio-stage-row${selectedTicker === row.ticker ? " active" : ""}`}
              onClick={() => onSelect(row.ticker)}
            >
              <div className="workflow-row-top">
                <div>
                  <div className="workflow-row-ticker">{row.ticker}</div>
                  <div className="workflow-row-company">{row.companyName}</div>
                </div>
                <div>{portfolioActionPill(row.recommendedAction)}</div>
              </div>
              <div className="workflow-row-meta">
                <span>{formatCurrency(row.currentPrice)}</span>
                <span>{formatCurrency(row.targetBuyPrice)}</span>
              </div>
              <p className="workflow-row-reason">{row.keyRisk || row.recommendedActionReason}</p>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function buildTickerOptions(rows: EquityDashboardRow[]): TickerOption[] {
  return [...rows]
    .map((row) => ({
      ticker: row.ticker,
      companyName: row.companyName,
    }))
    .sort((left, right) => left.ticker.localeCompare(right.ticker));
}

function buildTickerLimitNotice(): string {
  return `Portfolio limit reached. This portfolio can contain up to ${INVESTMENTS_PORTFOLIO_POSITION_LIMIT} tickers, so additional names were not added.`;
}

function filterTickerOptions(options: TickerOption[], searchTerm: string, selectedTickers: Set<string>): TickerOption[] {
  const normalized = searchTerm.trim().toLowerCase();
  return options.filter((option) => {
    if (selectedTickers.has(option.ticker)) {
      return false;
    }
    if (!normalized) {
      return true;
    }
    return option.ticker.toLowerCase().includes(normalized) || option.companyName.toLowerCase().includes(normalized);
  }).slice(0, 8);
}

function normalizeTickers(tickers: string[]): string[] {
  const normalized: string[] = [];
  const seen = new Set<string>();
  for (const rawTicker of tickers) {
    const ticker = rawTicker.trim().toUpperCase();
    if (ticker === "" || seen.has(ticker)) {
      continue;
    }
    seen.add(ticker);
    normalized.push(ticker);
  }
  return normalized;
}

function summarizePortfolio(portfolio: Portfolio): { activeResearch: number; candidates: number; owned: number; } {
  return portfolio.positions.reduce((counts, position) => {
    if (position.stage === "OWNED") {
      counts.owned += 1;
    } else if (position.stage === "ACTIVE_RESEARCH") {
      counts.activeResearch += 1;
    } else {
      counts.candidates += 1;
    }
    return counts;
  }, { activeResearch: 0, candidates: 0, owned: 0 });
}

function holdingStateFromStage(stage: PortfolioPositionStage): HoldingState {
  return stage === "OWNED" ? "OWNED" : "CANDIDATE";
}

function portfolioActionPill(value: string): JSX.Element {
  return <span className={`action-pill portfolio-action-${value.toLowerCase().replace(/_/g, "-")}`}>{value.replace(/_/g, " ")}</span>;
}

function formatCurrency(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "N/A";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);
}

function toDateInputValue(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
}

function formatDateLabel(value: string | null | undefined): string {
  if (!value) {
    return "Not set";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default PortfolioScreen;
